import { jwtDecode } from 'jwt-decode';
import { config, UserRole } from '../config';

export interface User {
  id: string | number;
  name?: string;
  email?: string;
  role?: string;
  [key: string]: unknown;
}

export interface Session {
  token: string;
  user: User | null;
  role: string | null;
}

/** Low-level storage helpers */

export function removeAuthKeys(): void {
  localStorage.removeItem(config.AUTH_LS_KEYS.TOKEN);
  localStorage.removeItem(config.AUTH_LS_KEYS.USER);
  localStorage.removeItem(config.AUTH_LS_KEYS.ROLE);
  localStorage.removeItem('menuAccess');
}

export function getAuthToken(): string | null {
  return localStorage.getItem(config.AUTH_LS_KEYS.TOKEN);
}

/** Token utilities */

export function isTokenValid(token: string | null): boolean {
  if (!token) return false;

  try {
    const { exp } = jwtDecode<{ exp: number }>(token);
    return Date.now() < exp * 1000;
  } catch {
    return false;
  }
}

/** Session-level helpers */

/**
 * Roles already warned about, so the notice fires once per distinct bad value instead of
 * on every render — `normalizeRole` runs in `Header` and `auth.context` on each pass.
 */
const warnedRoles = new Set<string>();

/**
 * Warns that `users.role` holds a value the UI does not know, which silently costs the
 * user every module their real role should grant. Deliberately uses `console.warn` rather
 * than `logger.warn`: the latter is a no-op outside development, and this misconfiguration
 * only ever shows up against live data.
 */
function warnUnknownRole(raw: string | undefined | null): void {
  const key = String(raw ?? '');
  if (warnedRoles.has(key)) return;
  warnedRoles.add(key);

  console.warn(
    `[auth] This login has no role (${JSON.stringify(raw)}), so it has no menu. ` +
      'Give it a role on Roles & Menu Access.',
  );
}

/**
 * The API returns roles capitalized (`Analyst`, `HR`, `Accounts`, `Admin`, `Employee`);
 * the UI keys off lower-case values. `hr` is kept as its own role rather than being
 * folded into `admin` so navigation and guards can distinguish the two — every route
 * guard that admits admin already admits hr explicitly.
 *
 * Anything else falls back to `analyst`. That fallback is intentionally permissive-free
 * rather than fatal, but it is warned about: a null or misspelled `users.role` otherwise
 * looks identical to a correctly-provisioned analyst.
 */
export function normalizeRole(role: string | undefined | null): UserRole {
  if (!role || !String(role).trim()) {
    warnUnknownRole(role);
    return config.USER_ROLES.EMPLOYEE;
  }
  // Roles are admin-defined since V30 (Roles & Menu Access), so any code is kept as it is;
  // what the role may open comes from its menu grants, not from this value.
  return String(role).toLowerCase().trim() as UserRole;
}

/**
 * Reads the stored role, tolerating both formats this key has carried.
 *
 * `saveSession` and `persistSession` write a bare lower-case string (`hr`), while the
 * retired provider in `services/AuthContext` wrote it JSON-encoded (`"admin"`). Call
 * sites that reached for the key with `JSON.parse(localStorage.getItem('role'))` threw
 * a SyntaxError against anything the current writer produces — `hr` is not valid JSON —
 * so the role silently became unreadable rather than merely wrong. Every consumer goes
 * through here so the storage format is decided in exactly one place.
 *
 * Returns `null` when no role is stored, which callers should treat as "not permitted"
 * rather than falling back to a default role.
 */
export function getStoredRole(): UserRole | null {
  const raw = localStorage.getItem(config.AUTH_LS_KEYS.ROLE);
  if (raw === null) return null;

  let value = raw;

  // Only a legacy value arrives quoted; leave anything else exactly as written.
  if (raw.startsWith('"')) {
    try {
      value = JSON.parse(raw);
    } catch {
      /* not valid JSON after all — fall through and read it as a plain string */
    }
  }

  return normalizeRole(value);
}

export function saveSession({ token, user }: { token: string; user: User }): Session {
  const role = normalizeRole(user?.role);

  localStorage.setItem(config.AUTH_LS_KEYS.TOKEN, token);
  localStorage.setItem(config.AUTH_LS_KEYS.USER, JSON.stringify(user));
  localStorage.setItem(config.AUTH_LS_KEYS.ROLE, role);

  return { token, user, role };
}

export function clearSession(): void {
  removeAuthKeys();
}

export function getStoredSession(): Session | null {
  const token = getAuthToken();
  if (!token) return null;

  const userRaw = localStorage.getItem(config.AUTH_LS_KEYS.USER);
  const role = localStorage.getItem(config.AUTH_LS_KEYS.ROLE);

  return {
    token,
    user: userRaw ? JSON.parse(userRaw) : null,
    role,
  };
}

/** Derived state helpers */

export function hasValidSession(): boolean {
  const token = getAuthToken();
  return isTokenValid(token);
}
